import {
  formatAuthFieldLabel,
  formatFormSnapshotSummary,
} from "./authFieldAnalytics";
import { isGranularEngagementEvent } from "./productAnalytics";
import * as XLSX from "xlsx";

const ENGAGEMENT_EXPORT_DATE = () => new Date().toISOString().slice(0, 10);

const triggerEngagementDownload = (blob, filename) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const escapeCsvCell = (val) => {
  const text = String(val ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const buildEngagementCsvBlob = (headers, rows) => {
  const csv = [headers, ...rows]
    .map((line) => line.map(escapeCsvCell).join(","))
    .join("\n");
  return new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
};

export const ENGAGEMENT_EVENT_LABEL_KEYS = {
  session_start: "engagement_page.eventSessionStart",
  page_view: "engagement_page.eventPageView",
  page_dwell: "engagement_page.eventPageDwell",
  page_interaction: "engagement_page.eventPageInteraction",
  login: "engagement_page.eventLogin",
  dashboard_opened: "engagement_page.eventDashboardOpened",
  dashboard_filter_changed: "engagement_page.eventDashboardFilterChanged",
  review_exported: "engagement_page.eventReviewExported",
  welcome_page_viewed: "engagement_page.eventWelcomePageViewed",
  welcome_widget_regenerated: "engagement_page.eventWelcomeWidgetRegenerated",
  welcome_widget_action: "engagement_page.eventWelcomeWidgetAction",
  welcome_reviews_opened: "engagement_page.eventWelcomeReviewsOpened",
  ai_report_opened: "engagement_page.eventAiReportOpened",
  advisor_session_started: "engagement_page.eventAdvisorSessionStarted",
  advisor_message_sent: "engagement_page.eventAdvisorMessageSent",
  kpi_view_action: "engagement_page.eventKpiViewAction",
  kpi_created: "engagement_page.eventKpiCreated",
  kpi_group_action: "engagement_page.eventKpiGroupAction",
  org_addon_action: "engagement_page.eventOrgAddonAction",
  addon_lock_viewed: "engagement_page.eventAddonLockViewed",
  addon_lock_cta_clicked: "engagement_page.eventAddonLockCtaClicked",
  form_field_focus: "engagement_page.eventFormFieldFocus",
  form_field_blur: "engagement_page.eventFormFieldBlur",
  form_field_fill: "engagement_page.eventFormFieldFill",
  auth_submit_attempt: "engagement_page.eventAuthSubmitAttempt",
  auth_oauth_click: "engagement_page.eventAuthOauthClick",
  login_attempt: "engagement_page.eventLoginAttempt",
  signup_attempt: "engagement_page.eventSignupAttempt",
  challenge_required: "engagement_page.eventChallengeRequired",
  auth_redirect_blocked: "engagement_page.eventAuthRedirectBlocked",
  challenge_shown: "engagement_page.eventChallengeShown",
  challenge_passed: "engagement_page.eventChallengePassed",
  challenge_failed: "engagement_page.eventChallengeFailed",
};

export const ENGAGEMENT_CATEGORY_LABEL_KEYS = {
  session: "engagement_page.categorySession",
  navigation: "engagement_page.categoryNavigation",
  auth: "engagement_page.categoryAuth",
  dashboard: "engagement_page.categoryDashboard",
  welcome: "engagement_page.categoryWelcome",
  kpi_view: "engagement_page.categoryKpiView",
  ai_services: "engagement_page.categoryAiServices",
  advisor: "engagement_page.categoryAdvisor",
  settings: "engagement_page.categorySettings",
  upsell: "engagement_page.categoryUpsell",
  export: "engagement_page.categoryExport",
  form: "engagement_page.categoryForm",
  other: "engagement_page.categoryOther",
};

export const ENGAGEMENT_PROPERTY_LABEL_KEYS = {
  previous_page: "engagement_page.propPreviousPage",
  referrer: "engagement_page.propReferrer",
  viewport_w: "engagement_page.propViewportW",
  viewport_h: "engagement_page.propViewportH",
  dashboard_name: "engagement_page.propDashboardName",
  view: "engagement_page.propView",
  brand: "engagement_page.propBrand",
  status: "engagement_page.propStatus",
  duration_ms: "engagement_page.propDurationMs",
  action_count: "engagement_page.propActionCount",
  exit_reason: "engagement_page.propExitReason",
  on_advisor_page: "engagement_page.propOnAdvisorPage",
  message_length: "engagement_page.propMessageLength",
  client_fingerprint: "engagement_page.propClientFingerprint",
  bot_score: "engagement_page.propBotScore",
  bot_suspect: "engagement_page.propBotSuspect",
  bot_signals: "engagement_page.propBotSignals",
  client_ip: "engagement_page.propClientIp",
  request_user_agent: "engagement_page.propRequestUserAgent",
  client_user_agent: "engagement_page.propClientUserAgent",
  field_name: "engagement_page.propFieldName",
  form_type: "engagement_page.propFormType",
  filled: "engagement_page.propFilled",
  text: "engagement_page.propFieldText",
  form_snapshot: "engagement_page.propFormSnapshot",
  mail: "engagement_page.propMail",
  given_name: "engagement_page.propGivenName",
  family_name: "engagement_page.propFamilyName",
  pwd_hint: "engagement_page.propPwdHint",
  success: "engagement_page.propSuccess",
  method: "engagement_page.propMethod",
  provider: "engagement_page.propProvider",
  reasons: "engagement_page.propReasons",
  error: "engagement_page.propError",
  action: "engagement_page.propAction",
  kpi_title: "engagement_page.propKpiTitle",
  metric_key: "engagement_page.propMetricKey",
  signal: "engagement_page.propSignal",
  filter_keys: "engagement_page.propFilterKeys",
  widget_name: "engagement_page.propWidgetName",
  metric_id: "engagement_page.propMetricId",
  topic_count: "engagement_page.propTopicCount",
  dashboard_count: "engagement_page.propDashboardCount",
  pivot_count: "engagement_page.propPivotCount",
  mode: "engagement_page.propMode",
  group_page: "engagement_page.propGroupPage",
  group_name: "engagement_page.propGroupName",
  filter_date: "engagement_page.propFilterDate",
  alert_mode: "engagement_page.propAlertMode",
  source: "engagement_page.propSource",
  label: "engagement_page.propLabel",
  topic_label: "engagement_page.propTopicLabel",
  team_id: "engagement_page.propTeamId",
  return_path: "engagement_page.propReturnPath",
  addon: "engagement_page.propAddon",
  cta: "engagement_page.propCta",
};

const KPI_ACTION_LABEL_KEYS = {
  metric_click: "engagement_page.kpiActionMetricClick",
  reviews_open: "engagement_page.kpiActionReviewsOpen",
  sparkline_click: "engagement_page.kpiActionSparklineClick",
  card_menu_info: "engagement_page.kpiActionCardMenuInfo",
  card_menu_edit: "engagement_page.kpiActionCardMenuEdit",
  card_menu_rename: "engagement_page.kpiActionCardMenuRename",
  card_menu_delete: "engagement_page.kpiActionCardMenuDelete",
  card_menu_move: "engagement_page.kpiActionCardMenuMove",
  alert_on: "engagement_page.kpiActionAlertOn",
  alert_off: "engagement_page.kpiActionAlertOff",
  alert_save: "engagement_page.kpiActionAlertSave",
};

const KPI_GROUP_ACTION_LABEL_KEYS = {
  add: "engagement_page.kpiGroupActionAdd",
  rename: "engagement_page.kpiGroupActionRename",
  delete: "engagement_page.kpiGroupActionDelete",
};

const WELCOME_ACTION_LABEL_KEYS = {
  info: "engagement_page.welcomeActionInfo",
  chart_click: "engagement_page.welcomeActionChartClick",
};

const ORG_ADDON_ACTION_LABEL_KEYS = {
  modal_opened: "engagement_page.orgAddonModalOpened",
  checkout_started: "engagement_page.orgAddonCheckoutStarted",
  checkout_mode: "engagement_page.orgAddonCheckoutMode",
  checkout_failed: "engagement_page.orgAddonCheckoutFailed",
  checkout_cancelled: "engagement_page.orgAddonCheckoutCancelled",
  checkout_retry: "engagement_page.orgAddonCheckoutRetry",
};

const ORG_ADDON_SOURCE_LABEL_KEYS = {
  plan: "engagement_page.orgAddonSourcePlan",
  kpi_view_footer: "engagement_page.orgAddonSourceKpiFooter",
  kpi_view_limit: "engagement_page.orgAddonSourceKpiLimit",
  alerts_page: "engagement_page.orgAddonSourceAlerts",
  dashboard_charts: "engagement_page.orgAddonSourceDashboard",
  dashboard_presentation: "engagement_page.orgAddonSourcePresentation",
  unknown: "engagement_page.orgAddonSourceUnknown",
};

const ADDON_LOCK_LABEL_KEYS = {
  market_intelligence: "engagement_page.addonMarketIntelligence",
  customer_journey: "engagement_page.addonCustomerJourney",
};

const ADDON_LOCK_CTA_LABEL_KEYS = {
  email: "engagement_page.addonLockCtaEmail",
  read_more: "engagement_page.addonLockCtaReadMore",
};

const METRIC_KEY_LABEL_KEYS = {
  volume: "engagement_page.metricKeyVolume",
  positive: "engagement_page.metricKeyPositive",
  appraisal: "engagement_page.metricKeyAppraisal",
  rating: "engagement_page.metricKeyRating",
};

const ID_ROUTE_PATTERNS = [
  [/^\/console\/Dashboard\/\d+/, "/console/Dashboard/:id"],
  [/^\/console\/DashboardData\/\d+/, "/console/DashboardData/:id"],
  [/^\/console\/insights\/\d+/, "/console/insights/:id"],
  [/^\/console\/journey\/\d+/, "/console/journey/:id"],
  [/^\/console\/combined-view\/\d+/, "/console/combined-view/:id"],
  [/^\/console\/topic-gap\/\d+/, "/console/topic-gap/:id"],
  [/^\/console\/top-problems\/\d+/, "/console/top-problems/:id"],
  [/^\/console\/ai-services\/auto-reports\/\d+/, "/console/ai-services/auto-reports/:id"],
  [/^\/console\/ai-services\/reports\/\d+/, "/console/ai-services/auto-reports/:id"],
  [/^\/console\/my-workspace\/\d+/, "/console/my-workspace/:id"],
  [/^\/console\/survey\/\d+/, "/console/survey/:id"],
  [/^\/console\/global_executive/, "/console/global_executive"],
];

export const normalizeEngagementPagePath = (pagePath) => {
  if (!pagePath) return "";
  const base = String(pagePath).split("?")[0].split("#")[0].replace(/\/$/, "") || "/";
  for (const [pattern, replacement] of ID_ROUTE_PATTERNS) {
    if (pattern.test(base)) return replacement;
  }
  if (base.startsWith("/settings/")) return "/settings/*";
  return base;
};

export const formatEngagementDateTime = (iso, locale = "tr-TR") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const formatEngagementTime = (iso, locale = "tr-TR") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const formatSessionIdShort = (sessionId) => {
  if (!sessionId) return "—";
  const text = String(sessionId);
  if (text.length <= 12) return text;
  return `${text.slice(0, 8)}…`;
};

export const getEventPreviousPage = (record) => {
  const props = record?.properties || {};
  const prev = props.previous_page;
  return prev != null && String(prev).trim() ? String(prev).trim() : null;
};

export const formatEngagementFlow = (record) => {
  const previous = getEventPreviousPage(record);
  const current = record?.page_path;
  if (previous && current && previous !== current) {
    return `${normalizeEngagementPagePath(previous)} → ${normalizeEngagementPagePath(current)}`;
  }
  if (current) return normalizeEngagementPagePath(current);
  return "—";
};

export const formatEngagementDuration = (ms, t) => {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 1000) return t("engagement_page.durationSubSecond");
  const totalSec = Math.round(value / 1000);
  if (totalSec < 60) {
    return t("engagement_page.durationSeconds", { count: totalSec });
  }
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (sec === 0) {
    return t("engagement_page.durationMinutes", { count: min });
  }
  return t("engagement_page.durationMinutesSeconds", { min, sec });
};

const ADVISOR_PAGE_PATH = "/console/advisor";
const ADVISOR_ACTIVE_ACTION_COUNT = 3;

export const isAdvisorEngagementPage = (pagePath) =>
  normalizeEngagementPagePath(pagePath) === ADVISOR_PAGE_PATH;

const sessionHasAdvisorMessageAfter = (pageViewEvent, sessionEvents) => {
  if (!isAdvisorEngagementPage(pageViewEvent?.page_path)) return false;
  const asc = [...(sessionEvents || [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const idx = asc.findIndex((e) => e.id === pageViewEvent.id);
  if (idx < 0) return false;
  for (let j = idx + 1; j < asc.length; j += 1) {
    const next = asc[j];
    if (next.event_name === "page_view") break;
    if (next.event_name === "advisor_message_sent") return true;
  }
  return false;
};

const applyAdvisorMessageEngagement = (pageViewEvent, sessionEvents, stats) => {
  if (!sessionHasAdvisorMessageAfter(pageViewEvent, sessionEvents)) return stats;
  const actionCount = Math.max(Number(stats.actionCount) || 0, ADVISOR_ACTIVE_ACTION_COUNT);
  return {
    ...stats,
    actionCount,
    engaged: true,
  };
};

export const getPageViewDwellStats = (pageViewEvent, sessionEvents) => {
  if (!pageViewEvent || pageViewEvent.event_name !== "page_view") {
    return { durationMs: null, actionCount: 0, engaged: false, inferred: false };
  }
  const asc = [...(sessionEvents || [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const idx = asc.findIndex((e) => e.id === pageViewEvent.id);
  if (idx < 0) {
    return { durationMs: null, actionCount: 0, engaged: false, inferred: false };
  }

  let durationMs = 0;
  let dwellActionCount = 0;
  let granularActionCount = 0;
  let hasDwellEvent = false;

  for (let j = idx + 1; j < asc.length; j += 1) {
    const next = asc[j];
    if (next.event_name === "page_view") {
      if (!hasDwellEvent) {
        const delta =
          new Date(next.created_at).getTime() -
          new Date(pageViewEvent.created_at).getTime();
        if (delta >= 1000) {
          const actionCount = Math.max(granularActionCount, dwellActionCount);
          return applyAdvisorMessageEngagement(pageViewEvent, sessionEvents, {
            durationMs: delta,
            actionCount,
            engaged: actionCount > 0,
            inferred: true,
          });
        }
      }
      break;
    }
    if (
      next.event_name === "page_dwell" &&
      next.page_path === pageViewEvent.page_path
    ) {
      hasDwellEvent = true;
      durationMs += Number(next.properties?.duration_ms) || 0;
      dwellActionCount += Number(next.properties?.action_count) || 0;
    } else if (isGranularEngagementEvent(next.event_name)) {
      granularActionCount += 1;
    }
  }

  if (!hasDwellEvent) {
    return applyAdvisorMessageEngagement(pageViewEvent, sessionEvents, {
      durationMs: null,
      actionCount: Math.max(granularActionCount, dwellActionCount),
      engaged: Math.max(granularActionCount, dwellActionCount) > 0,
      inferred: false,
    });
  }

  const actionCount = Math.max(granularActionCount, dwellActionCount);

  return applyAdvisorMessageEngagement(pageViewEvent, sessionEvents, {
    durationMs: durationMs > 0 ? durationMs : null,
    actionCount,
    engaged: actionCount > 0,
    inferred: false,
  });
};

export const getEngagementLevelKey = (actionCount) => {
  const n = Number(actionCount) || 0;
  if (n >= 3) return "engagement_page.engageLevelHigh";
  if (n >= 1) return "engagement_page.engageLevelMedium";
  return "engagement_page.engageLevelLow";
};

export const getEventDwellDisplay = (record, sessionEvents, t) => {
  if (record.event_name === "page_dwell") {
    const durationMs = Number(record.properties?.duration_ms);
    const actionCount = Number(record.properties?.action_count) || 0;
    return {
      durationMs: Number.isFinite(durationMs) ? durationMs : null,
      actionCount,
      engaged: actionCount > 0,
      inferred: false,
    };
  }
  if (record.event_name === "page_view") {
    return getPageViewDwellStats(record, sessionEvents);
  }
  return { durationMs: null, actionCount: 0, engaged: false, inferred: false };
};

const TIMELINE_NOISE_EVENTS = new Set([
  "page_dwell",
  "form_field_focus",
  "form_field_blur",
]);

/** Session / visitor timeline: hide noise and keep latest form fill per field. */
export const filterEngagementTimelineEvents = (events) => {
  const out = [];
  const fillIndexByField = new Map();
  for (const ev of events || []) {
    if (TIMELINE_NOISE_EVENTS.has(ev.event_name)) continue;
    if (ev.event_name === "form_field_fill") {
      const field = ev.properties?.field_name || "_";
      const existingIdx = fillIndexByField.get(field);
      if (existingIdx !== undefined) {
        out[existingIdx] = ev;
      } else {
        fillIndexByField.set(field, out.length);
        out.push(ev);
      }
      continue;
    }
    out.push(ev);
  }
  return out;
};

export const filterSessionTimelineEvents = (events) =>
  filterEngagementTimelineEvents(events);

export function formatVisitorFormSnapshotSummary(formSnapshot, t) {
  if (!formSnapshot || typeof formSnapshot !== "object") return null;
  return formatFormSnapshotSummary(formSnapshot, t);
}

export function summarizeVisitorAuthActivity(visitor, t) {
  const parts = [];
  const pages = visitor?.auth_pages || [];
  if (pages.length) {
    parts.push(pages.join(", "));
  }
  const formSummary = formatVisitorFormSnapshotSummary(visitor?.form_snapshot, t);
  if (formSummary) {
    parts.push(formSummary);
  }
  if (visitor?.login_attempt_count > 0) {
    parts.push(
      t("engagement_page.visitorLoginAttempts", {
        count: visitor.login_attempt_count,
        outcome: visitor.auth_success
          ? t("engagement_page.authOutcomeSuccess")
          : t("engagement_page.authOutcomeFailed"),
      }),
    );
  }
  if (visitor?.oauth_providers?.length) {
    parts.push(
      t("engagement_page.visitorOauthProviders", {
        providers: visitor.oauth_providers.join(", "),
      }),
    );
  }
  return parts.length ? parts.join(" · ") : t("engagement_page.visitorNoAuthActivity");
}

export const renderEngagementPropertyLabel = (key, t) =>
  t(ENGAGEMENT_PROPERTY_LABEL_KEYS[key] || key, key);

export const formatEngagementPropertyValue = (value, t) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).join(", ");
    }
    const snapshotSummary = formatFormSnapshotSummary(value, t);
    if (snapshotSummary) return snapshotSummary;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

function formatMetricKeyLabel(metricKey, t) {
  if (!metricKey) return null;
  const key = METRIC_KEY_LABEL_KEYS[metricKey] || null;
  return key ? t(key) : String(metricKey);
}

function formatActionLabel(action, map, t) {
  if (!action) return null;
  const labelKey = map[action];
  return labelKey ? t(labelKey) : String(action);
}

/** One-line human summary for event list / session timeline. */
export function formatEngagementEventDetail(record, t) {
  if (!record) return "—";
  const props = record.properties || {};
  const eventName = record.event_name;

  if (eventName === "kpi_view_action") {
    const parts = [
      formatActionLabel(props.action, KPI_ACTION_LABEL_KEYS, t),
      props.kpi_title,
      formatMetricKeyLabel(props.metric_key, t),
      props.filter_date,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "kpi_group_action") {
    const parts = [
      formatActionLabel(props.action, KPI_GROUP_ACTION_LABEL_KEYS, t),
      props.group_name,
      props.group_page != null ? `#${props.group_page}` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "kpi_created") {
    return t("engagement_page.kpiCreatedDetail", {
      topics: props.topic_count ?? 0,
      dashboards: props.dashboard_count ?? 0,
      pivots: props.pivot_count ?? 0,
      mode: props.mode || "combined",
    });
  }

  if (eventName === "welcome_widget_action") {
    const parts = [
      formatActionLabel(props.action, WELCOME_ACTION_LABEL_KEYS, t),
      props.widget_name,
      props.metric_id != null ? `metric ${props.metric_id}` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "welcome_reviews_opened") {
    const parts = [
      props.widget_name,
      props.label,
      props.topic_label,
      props.source,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "org_addon_action") {
    const actionLabel = formatActionLabel(
      props.action,
      ORG_ADDON_ACTION_LABEL_KEYS,
      t,
    );
    const sourceLabel = props.source
      ? t(
          ORG_ADDON_SOURCE_LABEL_KEYS[props.source] ||
            "engagement_page.orgAddonSourceUnknown",
          props.source,
        )
      : null;
    const parts = [
      actionLabel,
      sourceLabel,
      props.mode,
      props.step,
      props.error,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "addon_lock_viewed" || eventName === "addon_lock_cta_clicked") {
    const addonLabel = props.addon
      ? t(ADDON_LOCK_LABEL_KEYS[props.addon] || "engagement_page.addonUnknown", props.addon)
      : null;
    if (eventName === "addon_lock_cta_clicked") {
      const ctaLabel = props.cta
        ? t(ADDON_LOCK_CTA_LABEL_KEYS[props.cta] || props.cta)
        : null;
      return [addonLabel, ctaLabel].filter(Boolean).join(" · ") || "—";
    }
    return addonLabel || "—";
  }

  if (eventName === "page_interaction") {
    return props.signal
      ? t("engagement_page.pageInteractionDetail", { signal: props.signal })
      : "—";
  }

  if (eventName === "dashboard_filter_changed") {
    const keys = Array.isArray(props.filter_keys) ? props.filter_keys.join(", ") : "";
    return keys
      ? t("engagement_page.filterChangedDetail", { keys })
      : t("engagement_page.eventDashboardFilterChanged");
  }

  if (eventName === "advisor_message_sent") {
    const parts = [];
    if (props.message_length) {
      parts.push(t("engagement_page.advisorMessageDetail", { length: props.message_length }));
    }
    if (props.on_advisor_page) {
      parts.push(t("engagement_page.propOnAdvisorPage"));
    }
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "form_field_fill") {
    const parts = [];
    const snapshotSummary = formatFormSnapshotSummary(props.form_snapshot, t);
    if (snapshotSummary) {
      parts.push(snapshotSummary);
    } else {
      const fieldLabel = props.field_name
        ? formatAuthFieldLabel(props.field_name, t)
        : null;
      const value =
        props.text ||
        props.mail ||
        props.given_name ||
        props.family_name ||
        props.pwd_hint ||
        null;
      if (fieldLabel && value) {
        parts.push(`${fieldLabel}: ${value}`);
      } else if (fieldLabel) {
        parts.push(fieldLabel);
        if (props.filled === false) {
          parts.push(t("engagement_page.fieldCleared", "Cleared"));
        } else if (props.filled) {
          parts.push(t("engagement_page.fieldFilled", "Filled"));
        }
      } else if (value) {
        parts.push(String(value));
      }
    }
    if (props.form_type) {
      parts.push(String(props.form_type));
    }
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "form_field_focus" || eventName === "form_field_blur") {
    const parts = [];
    if (props.field_name) {
      parts.push(formatAuthFieldLabel(props.field_name, t));
    }
    if (props.filled === false) {
      parts.push(t("engagement_page.fieldCleared", "Cleared"));
    } else if (props.filled) {
      parts.push(t("engagement_page.fieldFilled", "Filled"));
    }
    if (props.form_type) {
      parts.push(String(props.form_type));
    }
    return parts.length ? parts.join(" · ") : "—";
  }

  if (eventName === "auth_redirect_blocked") {
    const path = props.blocked_path || record.page_path;
    return path
      ? t("engagement_page.authRedirectBlockedDetail", { path })
      : "—";
  }

  if (
    eventName === "login_attempt" ||
    eventName === "signup_attempt" ||
    eventName === "auth_submit_attempt"
  ) {
    const parts = [];
    if (props.form_type) {
      parts.push(String(props.form_type));
    }
    if (props.success === true) {
      parts.push(t("engagement_page.authOutcomeSuccess", "Success"));
    } else if (props.success === false) {
      parts.push(t("engagement_page.authOutcomeFailed", "Failed"));
    }
    if (props.method) {
      parts.push(String(props.method));
    }
    const snapshotSummary =
      formatFormSnapshotSummary(props.form_snapshot, t) ||
      (props.mail ? `${formatAuthFieldLabel("email", t)}: ${props.mail}` : null);
    if (snapshotSummary) {
      parts.push(snapshotSummary);
    }
    return parts.length ? parts.join(" · ") : "—";
  }

  const priorityKeys = [
    "action",
    "kpi_title",
    "metric_key",
    "widget_name",
    "signal",
    "label",
    "dashboard_name",
    "filter_date",
    "alert_mode",
    "mode",
  ];
  const parts = priorityKeys
    .map((key) => {
      const val = props[key];
      if (val == null || val === "") return null;
      if (key === "metric_key") return formatMetricKeyLabel(val, t);
      if (Array.isArray(val)) return val.join(", ");
      return String(val);
    })
    .filter(Boolean);

  if (parts.length) return parts.join(" · ");

  if (record.resource_type && record.resource_id) {
    return `${record.resource_type}:${record.resource_id}`;
  }

  return "—";
}

export const buildEngagementEventExportTable = (items, t, locale = "tr-TR") => {
  const headers = [
    t("engagement_page.csvDate"),
    t("engagement_page.colUser"),
    t("engagement_page.colEvent"),
    t("engagement_page.colCategory"),
    t("engagement_page.colFlow"),
    t("engagement_page.colDuration"),
    t("engagement_page.colEngagement"),
    t("engagement_page.colPage"),
    t("engagement_page.colResource"),
    t("engagement_page.colSession"),
    t("engagement_page.colIp"),
  ];

  const rows = (items || []).map((row) => {
    const eventLabel = t(
      ENGAGEMENT_EVENT_LABEL_KEYS[row.event_name] || row.event_name,
      row.event_name,
    );
    const categoryLabel = t(
      ENGAGEMENT_CATEGORY_LABEL_KEYS[row.event_category] || row.event_category,
      row.event_category,
    );
    const resource =
      row.resource_type && row.resource_id
        ? `${row.resource_type}:${row.resource_id}`
        : row.resource_id || "";
    const dwell =
      row.event_name === "page_dwell"
        ? getEventDwellDisplay(row, [row], t)
        : row.event_name === "page_view"
          ? getPageViewDwellStats(row, items)
          : { durationMs: null, actionCount: 0, engaged: false };
    return [
      formatEngagementDateTime(row.created_at, locale),
      row.user_display || row.user_email || row.user_id || "",
      eventLabel,
      categoryLabel,
      formatEngagementFlow(row),
      formatEngagementDuration(dwell.durationMs, t),
      dwell.actionCount > 0
        ? t(getEngagementLevelKey(dwell.actionCount))
        : dwell.durationMs
          ? t("engagement_page.engageLevelLow")
          : "",
      row.page_path || "",
      resource,
      row.session_id || "",
      row.client_ip || row.properties?.client_ip || "",
    ];
  });

  return { headers, rows };
};

export const buildEngagementSessionExportTable = (sessions, t, locale = "tr-TR") => {
  const headers = [
    t("engagement_page.colSession"),
    t("engagement_page.colUser"),
    t("engagement_page.sessionStarted"),
    t("engagement_page.colDate"),
    t("engagement_page.colEvent"),
    t("engagement_page.colFlow"),
    t("engagement_page.colDuration"),
    t("engagement_page.colEngagement"),
    t("engagement_page.colPage"),
  ];

  const rows = [];
  (sessions || []).forEach((session) => {
    const visible = filterSessionTimelineEvents(session.events);
    visible.forEach((ev) => {
      const dwell = getEventDwellDisplay(ev, session.events, t);
      rows.push([
        session.session_id || "",
        session.user_display || session.user_email || session.user_id || "",
        formatEngagementDateTime(session.started_at, locale),
        formatEngagementDateTime(ev.created_at, locale),
        t(ENGAGEMENT_EVENT_LABEL_KEYS[ev.event_name] || ev.event_name, ev.event_name),
        formatEngagementFlow(ev),
        formatEngagementDuration(dwell.durationMs, t),
        dwell.actionCount > 0
          ? t(getEngagementLevelKey(dwell.actionCount))
          : dwell.durationMs
            ? t("engagement_page.engageLevelLow")
            : "",
        ev.page_path || "",
      ]);
    });
  });

  return { headers, rows };
};

export const exportEngagementCsv = (items, t, locale = "tr-TR") => {
  const { headers, rows } = buildEngagementEventExportTable(items, t, locale);
  triggerEngagementDownload(
    buildEngagementCsvBlob(headers, rows),
    `pivony-engagement-${ENGAGEMENT_EXPORT_DATE()}.csv`,
  );
};

export const exportEngagementSessionsCsv = (sessions, t, locale = "tr-TR") => {
  const { headers, rows } = buildEngagementSessionExportTable(sessions, t, locale);
  triggerEngagementDownload(
    buildEngagementCsvBlob(headers, rows),
    `pivony-engagement-sessions-${ENGAGEMENT_EXPORT_DATE()}.csv`,
  );
};

export const exportEngagementJson = ({
  viewMode,
  items,
  sessions,
  filters,
}) => {
  const payload = {
    exported_at: new Date().toISOString(),
    view_mode: viewMode,
    filters: filters || {},
    ...(viewMode === "sessions"
      ? { sessions: sessions || [] }
      : { events: items || [] }),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json;charset=utf-8;",
  });
  const suffix = viewMode === "sessions" ? "sessions" : "events";
  triggerEngagementDownload(
    blob,
    `pivony-engagement-${suffix}-${ENGAGEMENT_EXPORT_DATE()}.json`,
  );
};

const exportEngagementTableExcel = (headers, rows, sheetName, filename) => {
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerEngagementDownload(blob, filename);
};

export const exportEngagementExcel = (items, t, locale = "tr-TR") => {
  const { headers, rows } = buildEngagementEventExportTable(items, t, locale);
  exportEngagementTableExcel(
    headers,
    rows,
    "Events",
    `pivony-engagement-${ENGAGEMENT_EXPORT_DATE()}.xlsx`,
  );
};

export const exportEngagementSessionsExcel = (sessions, t, locale = "tr-TR") => {
  const { headers, rows } = buildEngagementSessionExportTable(sessions, t, locale);
  exportEngagementTableExcel(
    headers,
    rows,
    "Sessions",
    `pivony-engagement-sessions-${ENGAGEMENT_EXPORT_DATE()}.xlsx`,
  );
};

export const maxDailyActive = (dailyActive) =>
  Math.max(1, ...(dailyActive || []).map((d) => Number(d.active_users || 0)));
